import { z } from 'zod';

import {
  type AnswerReading,
  readAnswerDocument,
  refusedShape,
  unfencedAnswer,
} from './answer-document.ts';
import { answerWire } from './answer-wire.ts';
import { BRIEF_CARD_QUOTE_LIMIT, MAX_BRIEF_PASSAGE_CHARS } from './brief.ts';
import {
  type BriefHandles,
  type BriefProseScope,
  type BriefProseSegment,
  briefValuesOf,
  boundedBriefProse,
  briefTokenInScope,
  resolveBriefToken,
  rqProseScope,
  valuedBriefSegment,
  wholeStudyProseScope,
} from './brief-answer.ts';
import {
  type BriefProseExtension,
  type BriefToken,
  type CountRef,
  MAX_RICH_TEXT_SEGMENTS,
  briefToken,
  countRefSchema,
  gatedBriefProse,
  type RichText,
  renderRichText,
  richTextSchema,
  richTextSegmentSchema,
} from './brief-counts.ts';
import type { StudyEvidence } from './brief-evidence.ts';

/**
 * Where a follow-up's answer stands. `pending` while the worker answers it; `answered` once a
 * readable answer is stored, after which the answer never changes; `unavailable` when no answer
 * could be had (the budget, a timeout, no model) and `unreadable` when the answer failed its
 * grammar, both of which a retry moves back to `pending`.
 */
export const FOLLOW_UP_ANSWER_STATUSES = [
  'pending',
  'answered',
  'unavailable',
  'unreadable',
] as const;
export type FollowUpAnswerStatus = (typeof FOLLOW_UP_ANSWER_STATUSES)[number];

/**
 * Where a requested "Update the brief" stands while it has no outcome: `pending` while the worker
 * revises the Brief, `unavailable` or `unreadable` when that revision failed. A committed update is
 * an outcome row, not a status.
 */
export const FOLLOW_UP_UPDATE_STATUSES = ['pending', 'unavailable', 'unreadable'] as const;
export type FollowUpUpdateStatus = (typeof FOLLOW_UP_UPDATE_STATUSES)[number];

/** How an answered follow-up lands in the study record. Each follow-up gets one, once. */
export const FOLLOW_UP_OUTCOME_KINDS = ['update_brief', 'note', 'log'] as const;
export type FollowUpOutcomeKind = (typeof FOLLOW_UP_OUTCOME_KINDS)[number];

/** What answers a follow-up: the same kind of synthesizer that wrote the study's published Brief. */
export const FOLLOW_UP_ANSWERERS = ['fixture', 'model'] as const;
export type FollowUpAnswerer = (typeof FOLLOW_UP_ANSWERERS)[number];

/** The longest follow-up question a study keeps, in characters. */
export const MAX_FOLLOW_UP_QUESTION_CHARS = 2000;

/**
 * The queue a follow-up is answered on, retried with a dead letter as the Brief's synthesis is: its
 * job settles every outcome and throws only on the unexpected, so a retry is for a worker that died.
 */
export const QUEUE_BRIEF_FOLLOW_UP = 'brief.follow-up';

/** Where a follow-up's answer job lands once its retries are spent; its handler settles the answer. */
export const QUEUE_BRIEF_FOLLOW_UP_DEAD_LETTER = 'brief.follow-up.dead-letter';

/** The queue an "Update the brief" chosen from a follow-up is written on, retried as the answer's is. */
export const QUEUE_BRIEF_FOLLOW_UP_REVISE = 'brief.follow-up.revise';

/** Where an update's job lands once its retries are spent; its handler settles the update. */
export const QUEUE_BRIEF_FOLLOW_UP_REVISE_DEAD_LETTER = 'brief.follow-up.revise.dead-letter';

/**
 * A follow-up job: the follow-up and the request it answers — its answer's or its update's, the job
 * sent under that id — so a job for a request a retry replaced does nothing.
 */
export const followUpJobPayloadSchema = z.strictObject({
  followUpId: z.uuid(),
  requestId: z.uuid(),
});
export type FollowUpJobPayload = z.infer<typeof followUpJobPayloadSchema>;

/**
 * Why a follow-up's answer, or its update, is unavailable: the follow-up budget spent, a call that
 * timed out, no model to answer, a call priced above one turn's cap, the study's evidence not
 * readable, or a request the queue gave up on. Each one keeps the question and offers Try again.
 */
export const FOLLOW_UP_UNAVAILABLE_REASONS = [
  'budget_spent',
  'timed_out',
  'model_unavailable',
  'over_turn_cap',
  'evidence_unavailable',
  'interrupted',
] as const;
export type FollowUpUnavailableReason = (typeof FOLLOW_UP_UNAVAILABLE_REASONS)[number];

/** Why a follow-up has no answer or no update: unavailable, or `unreadable` when what came back failed its grammar. */
export type FollowUpFailureReason = FollowUpUnavailableReason | 'unreadable';

/**
 * Where a follow-up stands, derived from its row and its outcome and never stored as one column:
 * `answering` while its answer is pending, `answer_failed` when there is none, `choosing` while an
 * answer waits for its outcome (including after a failed update), `updating` while an update is
 * pending, and `finalized` once it has its one outcome.
 */
export const FOLLOW_UP_STATES = [
  'answering',
  'answer_failed',
  'choosing',
  'updating',
  'finalized',
] as const;
export type FollowUpState = (typeof FOLLOW_UP_STATES)[number];

/**
 * How a follow-up landed in the study record: its kind, the card a note sits under, and for an
 * update the Brief version it left and whether it changed the Brief. An update whose revision
 * changed nothing is still an outcome, at the version it was checked against.
 */
export type FollowUpOutcome =
  | { kind: 'note'; rqId: string }
  | { kind: 'log' }
  | { kind: 'update_brief'; briefVersion: number; changed: boolean };

/** Where a follow-up stands (`FOLLOW_UP_STATES`). */
export const followUpState = (
  row: { answerStatus: FollowUpAnswerStatus; updateStatus: FollowUpUpdateStatus | null },
  outcome: FollowUpOutcome | null,
): FollowUpState => {
  if (outcome !== null) {
    return 'finalized';
  }
  switch (row.answerStatus) {
    case 'pending':
      return 'answering';
    case 'unavailable':
    case 'unreadable':
      return 'answer_failed';
    case 'answered':
      return row.updateStatus === 'pending' ? 'updating' : 'choosing';
  }
};

/** Whether a follow-up's answer can be asked for again: only once it has failed. */
export const followUpRetryable = (state: FollowUpState): boolean => state === 'answer_failed';

/**
 * Why a follow-up request is refused, each answered with its HTTP status:
 *
 * - `follow_up_id_conflict` (409) — the id is another follow-up's, of another study or question.
 * - `follow_up_answer_running` (409) — the study already has a follow-up being answered.
 * - `follow_up_not_answered` (409) — an outcome chosen for a follow-up with no answer.
 * - `follow_up_already_finalized` (409) — another outcome than the one it has.
 * - `follow_up_updating` (409) — a note or log while its update is being written.
 * - `follow_up_note_needs_card` (400) — a note with no card named, scoped or related.
 * - `follow_up_unknown_card` (400) — a card the current Brief does not have.
 * - `follow_up_not_retryable` (409) — Try again on a follow-up whose answer has not failed.
 * - `brief_method_locked` (409) — a revision that would change the Brief's Method.
 * - `brief_chat_is_follow_ups` (400) — a turn sent on the Brief's thread, where every question is
 *   a follow-up.
 */
export const FOLLOW_UP_REFUSAL_CODES = [
  'follow_up_id_conflict',
  'follow_up_answer_running',
  'follow_up_not_answered',
  'follow_up_already_finalized',
  'follow_up_updating',
  'follow_up_note_needs_card',
  'follow_up_unknown_card',
  'follow_up_not_retryable',
  'brief_method_locked',
  'brief_chat_is_follow_ups',
] as const;
export type FollowUpRefusalCode = (typeof FOLLOW_UP_REFUSAL_CODES)[number];

/**
 * The card a note sits under: the one the request names, else the card the question was asked
 * about, else the research question the answer relates to; `null` when none names one.
 */
export const followUpNoteTarget = ({
  requested,
  scope,
  relatesTo,
}: {
  requested: string | null;
  scope: string | null;
  relatesTo: string | null;
}): string | null => requested ?? scope ?? relatesTo;

/** What choosing an outcome does to a follow-up (`decideFollowUpOutcome`). */
export type FollowUpOutcomeDecision =
  | { act: 'insert'; outcome: Extract<FollowUpOutcome, { kind: 'note' | 'log' }> }
  | { act: 'request_update' }
  | { act: 'dedupe' }
  | { refuse: FollowUpRefusalCode };

/**
 * What choosing `request` does to a follow-up in `state` whose outcome, if it has one, is
 * `outcome`. `request.rqId` is the card the request named, or `null` when it named none;
 * `followUp` is the card the question was asked about and the research question its answer
 * relates to.
 *
 * - With no answer — `answering`, `answer_failed` — every choice is `follow_up_not_answered`.
 * - Once `finalized`, the outcome it has again is a `dedupe` — for a note, one naming no card or
 *   the note's own — and any other is `follow_up_already_finalized`: an outcome never changes.
 * - While `updating`, the update again is a `dedupe`; a note or log is `follow_up_updating`.
 * - While `choosing`, a note is inserted under `followUpNoteTarget`'s card (with none,
 *   `follow_up_note_needs_card`), a log line is inserted, and an update is requested — again,
 *   after a failed one.
 */
export const decideFollowUpOutcome = (
  state: FollowUpState,
  outcome: FollowUpOutcome | null,
  request: { kind: FollowUpOutcomeKind; rqId: string | null },
  followUp: { scopeRqId: string | null; relatesToRqId: string | null },
): FollowUpOutcomeDecision => {
  switch (state) {
    case 'answering':
    case 'answer_failed':
      return { refuse: 'follow_up_not_answered' };
    case 'finalized': {
      if (outcome === null) {
        throw new Error('a finalized follow-up has its outcome');
      }
      const same =
        outcome.kind === 'note'
          ? request.kind === 'note' && (request.rqId === null || request.rqId === outcome.rqId)
          : outcome.kind === request.kind;
      return same ? { act: 'dedupe' } : { refuse: 'follow_up_already_finalized' };
    }
    case 'updating':
      return request.kind === 'update_brief' ? { act: 'dedupe' } : { refuse: 'follow_up_updating' };
    case 'choosing':
      switch (request.kind) {
        case 'update_brief':
          return { act: 'request_update' };
        case 'log':
          return { act: 'insert', outcome: { kind: 'log' } };
        case 'note': {
          const rqId = followUpNoteTarget({
            requested: request.rqId,
            scope: followUp.scopeRqId,
            relatesTo: followUp.relatesToRqId,
          });
          return rqId === null
            ? { refuse: 'follow_up_note_needs_card' }
            : { act: 'insert', outcome: { kind: 'note', rqId } };
        }
      }
  }
};

/** The most themes and options one `{{anyof:…}}` may name. A storage bound. */
export const MAX_FOLLOW_UP_ANYOF_REFS = 40;

/**
 * A count only a follow-up answer holds: how many distinct people are in any of the named themes
 * and options. Never a sum — a person in two of them is one person.
 */
const anyOfSegmentSchema = z.strictObject({
  t: z.literal('anyof'),
  refs: z.array(countRefSchema).min(2).max(MAX_FOLLOW_UP_ANYOF_REFS),
  value: z.int().nonnegative(),
});

/**
 * A follow-up answer's text, as stored: a Brief passage's segments, and the participant-union count
 * (`anyof`) that only a follow-up answer may hold. No Brief content reads this schema, so no Brief
 * can hold an `anyof`.
 */
export const followUpTextSchema = z
  .array(z.discriminatedUnion('t', [...richTextSegmentSchema.options, anyOfSegmentSchema]))
  .min(1)
  .max(MAX_RICH_TEXT_SEGMENTS);
export type FollowUpText = z.infer<typeof followUpTextSchema>;
export type FollowUpTextSegment = FollowUpText[number];

/** A follow-up answer as a reader sees it: the words, with each count and option wording in place. */
export const renderFollowUpText = (text: FollowUpText): string =>
  text
    .map((segment) => (segment.t === 'anyof' ? String(segment.value) : renderRichText([segment])))
    .join('');

/** A quote a follow-up answer shows, its words exactly the evidence's. */
const followUpQuoteSchema = z.strictObject({
  quote_id: z.string().min(1),
  participant: z.string().min(1),
  text: z.string().min(1),
});
export type FollowUpQuote = z.infer<typeof followUpQuoteSchema>;

export const FOLLOW_UP_ANSWER_SCHEMA_VERSION = 1;

/**
 * A follow-up's answer as stored on its row: whether the study could answer it, the text, up to
 * `BRIEF_CARD_QUOTE_LIMIT` quotes, the research question it relates to, and the outcome Sigora
 * suggests. An answer the study cannot give carries no quotes and suggests a log line.
 */
export const storedFollowUpAnswerSchema = z
  .strictObject({
    schema_version: z.literal(FOLLOW_UP_ANSWER_SCHEMA_VERSION),
    status: z.enum(['answered', 'not_answerable']),
    text: followUpTextSchema,
    quotes: z.array(followUpQuoteSchema).max(BRIEF_CARD_QUOTE_LIMIT),
    relates_to_rq_id: z.uuid().nullable(),
    suggestion: z.enum(FOLLOW_UP_OUTCOME_KINDS),
  })
  .superRefine((answer, context) => {
    if (answer.status === 'not_answerable' && answer.quotes.length > 0) {
      context.addIssue({
        code: 'custom',
        path: ['quotes'],
        message: 'an answer the study cannot give quotes nobody',
      });
    }
    if (answer.status === 'not_answerable' && answer.suggestion !== 'log') {
      context.addIssue({
        code: 'custom',
        path: ['suggestion'],
        message: 'an answer the study cannot give suggests a log line',
      });
    }
  });
export type StoredFollowUpAnswer = z.infer<typeof storedFollowUpAnswerSchema>;

/** A stored answer, read, or `null` when it is not one this build reads. */
export const parseStoredFollowUpAnswer = (value: unknown): StoredFollowUpAnswer | null => {
  const parsed = storedFollowUpAnswerSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

/**
 * The longest text a follow-up answer shows, in characters of its own words. Longer is cut at a
 * word, not refused.
 */
export const MAX_FOLLOW_UP_ANSWER_CHARS = MAX_BRIEF_PASSAGE_CHARS;

/**
 * Why an answer is unreadable, one rule per kind of breach:
 *
 * - `document` — not one JSON document, or no words of its own around its counts.
 * - `shape` — not the answer format's shape (`FOLLOW_UP_ANSWER_WIRE_SHAPE`).
 * - `gate` — a character outside the canonical form, a bare numeral, a quotation, or a malformed
 *   token (`gatedBriefProse`).
 * - `token` — a token or `anyof` naming a poll, option or theme the study does not have.
 * - `scope` — a token, `anyof`, quote or related research question outside the card asked about.
 * - `anyof` — an `anyof` naming fewer than two different themes and options, or too many.
 * - `quote` — a quote the study does not have, one named twice, or too many.
 * - `relates_to` — a research question the study does not have.
 * - `not_answerable` — an answer the study cannot give that quotes someone or suggests other than a
 *   log line.
 * - `segments` — more segments than a stored passage holds.
 */
export const FOLLOW_UP_ANSWER_RULES = [
  'document',
  'shape',
  'gate',
  'token',
  'scope',
  'anyof',
  'quote',
  'relates_to',
  'not_answerable',
  'segments',
] as const;
export type FollowUpAnswerRule = (typeof FOLLOW_UP_ANSWER_RULES)[number];

/** The answer a follow-up answerer gives, as its instructions show it. */
export const FOLLOW_UP_ANSWER_EXAMPLE =
  '{"status": "answered" | "not_answerable", "answer": "<a few sentences>", "quotes": ["Q12", ...], "relates_to": "RQ1" | null, "suggestion": "update_brief" | "note" | "log"}';

/**
 * The answer format's shape, which `parseFollowUpAnswer` reads with and the provider is asked to
 * hold the answer to (`FOLLOW_UP_ANSWER_WIRE`): every key required, every object closed, and no
 * bound. The parser holds the handles, the quote limit and every rule of the words.
 */
export const FOLLOW_UP_ANSWER_WIRE_SHAPE = z.strictObject({
  status: z.enum(['answered', 'not_answerable']),
  answer: z.string(),
  quotes: z.array(z.string()),
  relates_to: z.string().nullable(),
  suggestion: z.enum(FOLLOW_UP_OUTCOME_KINDS),
});

/** The wire a follow-up answer answers under. */
export const FOLLOW_UP_ANSWER_WIRE = answerWire('follow_up_answer', FOLLOW_UP_ANSWER_WIRE_SHAPE);

const ANYOF_HANDLE = /^(?:THEME([1-9]\d*)|POLL([1-9]\d*)\.([1-9]\d*))$/;

/**
 * `{{anyof:THEME1,THEME4,POLL2.1}}`: the people in any of the named themes and options, each once.
 * Each handle read as the count token it names, in the order written, or `null` when one is not a
 * theme's or an option's.
 */
const anyOfToken: BriefProseExtension<BriefToken[]> = {
  pattern: /\{\{anyof:[^{}]*\}\}/,
  read: (token) => {
    const named: BriefToken[] = [];
    const list = token.slice('{{anyof:'.length, -'}}'.length).replace(/^ +| +$/g, '');
    for (const handle of list.split(/ *, */)) {
      const match = ANYOF_HANDLE.exec(handle);
      if (match === null) {
        return null;
      }
      const [, theme, poll, option] = match;
      named.push(
        theme === undefined
          ? { kind: 'count', poll: Number(poll), option: Number(option) }
          : { kind: 'count', theme: Number(theme) },
      );
    }
    return named;
  },
};

/** Writes an `anyof` token, so a fixture and a test spell it the way the grammar reads it. */
export const followUpAnyOfToken = (handles: readonly string[]): string =>
  `{{anyof:${handles.join(',')}}}`;

/**
 * How many distinct people are in any of `refs` — the union of each theme's and option's
 * `members`, never the sum of their counts. A ref the evidence does not hold adds nobody.
 */
export const followUpAnyOfCount = (evidence: StudyEvidence, refs: readonly CountRef[]): number => {
  const people = new Set<string>();
  for (const ref of refs) {
    for (const rq of evidence.rqs) {
      const members =
        'theme' in ref
          ? rq.themes.status === 'available'
            ? rq.themes.themes.find((theme) => theme.theme_id === ref.theme)?.members
            : undefined
          : rq.polls
              .find((poll) => poll.dg_question_id === ref.poll)
              ?.options.find((option) => option.option_id === ref.option)?.members;
      for (const member of members ?? []) {
        people.add(member);
      }
    }
  }
  return people.size;
};

class Unreadable extends Error {
  readonly rule: FollowUpAnswerRule;

  constructor(rule: FollowUpAnswerRule) {
    super(rule);
    this.rule = rule;
  }
}

/**
 * A follow-up answerer's answer, read against the handles its prompt was numbered with and the
 * evidence they number, or the rule it broke. Held to the Brief synthesis's rules:
 *
 * - one JSON document in the answer format (`FOLLOW_UP_ANSWER_EXAMPLE`), optionally fenced;
 * - its words through the numeral gate, every count a token, with `{{anyof:…}}` read beside the
 *   Brief's tokens — two different themes or options at least, resolved to the union of their
 *   people (`followUpAnyOfCount`);
 * - asked about a card (`scopeRqId`), its tokens, quotes and related research question that card's
 *   own, as a card's prose is held; asked of the whole study, any of the study's;
 * - at most `BRIEF_CARD_QUOTE_LIMIT` quotes, by handle, each once, their words the evidence's;
 * - an answer the study cannot give quotes nobody and suggests a log line.
 *
 * Over-long words are cut (`MAX_FOLLOW_UP_ANSWER_CHARS`), never refused; everything else that
 * fails is refused whole, and nothing is rewritten. A `scopeRqId` the handles do not number is the
 * caller's bug, and throws.
 */
export const parseFollowUpAnswer = (
  raw: string,
  {
    handles,
    evidence,
    scopeRqId,
  }: { handles: BriefHandles; evidence: StudyEvidence; scopeRqId: string | null },
): AnswerReading<StoredFollowUpAnswer, FollowUpAnswerRule> => {
  const scopeRq = scopeRqId === null ? null : handles.rqs.find((rq) => rq.rq_id === scopeRqId);
  if (scopeRq === undefined) {
    throw new Error('a follow-up is scoped to a research question its handles number');
  }

  try {
    const document = readAnswerDocument(unfencedAnswer(raw));
    if (document === null) {
      return { ok: false, rule: 'document' };
    }
    const parsed = FOLLOW_UP_ANSWER_WIRE_SHAPE.safeParse(document.value);
    if (!parsed.success) {
      return refusedShape(parsed.error);
    }
    const { data } = parsed;

    const everything = wholeStudyProseScope(handles, { themes: 'kept' });
    const scope = scopeRq === null ? everything : rqProseScope(scopeRq, { themes: 'kept' });
    const text = readFollowUpText(data.answer, { handles, evidence, everything, scope });

    if (
      data.quotes.length > BRIEF_CARD_QUOTE_LIMIT ||
      new Set(data.quotes).size !== data.quotes.length
    ) {
      throw new Unreadable('quote');
    }
    const quotes = data.quotes.map((handle): FollowUpQuote => {
      const quote = handles.quotes.get(handle);
      if (quote === undefined) {
        throw new Unreadable('quote');
      }
      if (scopeRq !== null && !scopeRq.quotes.includes(handle)) {
        throw new Unreadable('scope');
      }
      return { quote_id: quote.quote_id, participant: quote.participant, text: quote.text };
    });

    let relatesTo: string | null = null;
    if (data.relates_to !== null) {
      const rq = handles.rqs.find((each) => each.handle === data.relates_to);
      if (rq === undefined) {
        throw new Unreadable('relates_to');
      }
      if (scopeRq !== null && rq.rq_id !== scopeRq.rq_id) {
        throw new Unreadable('scope');
      }
      relatesTo = rq.rq_id;
    }

    if (data.status === 'not_answerable' && (quotes.length > 0 || data.suggestion !== 'log')) {
      throw new Unreadable('not_answerable');
    }

    return {
      ok: true,
      value: storedFollowUpAnswerSchema.parse({
        schema_version: FOLLOW_UP_ANSWER_SCHEMA_VERSION,
        status: data.status,
        text,
        quotes,
        relates_to_rq_id: relatesTo,
        suggestion: data.suggestion,
      }),
    };
  } catch (error) {
    if (error instanceof Unreadable) {
      return { ok: false, rule: error.rule };
    }
    throw error;
  }
};

/**
 * An answer's words, read: gated — with the `anyof` token when `evidence` is given to count it
 * over, without it when `evidence` is `null` — each token resolved first against the whole study
 * (`token` when it names nothing there) and then against `scope` (`scope` when it reaches outside),
 * its values filled in from the handles and the evidence, cut to `MAX_FOLLOW_UP_ANSWER_CHARS`.
 */
const readFollowUpText = (
  answer: string,
  {
    handles,
    evidence,
    everything,
    scope,
  }: {
    handles: BriefHandles;
    evidence: StudyEvidence | null;
    everything: BriefProseScope;
    scope: BriefProseScope;
  },
): FollowUpText => {
  const trimmed = answer.trim();
  if (trimmed.length === 0) {
    throw new Unreadable('document');
  }
  const parts = evidence === null ? gatedBriefProse(trimmed) : gatedBriefProse(trimmed, anyOfToken);
  if (parts === null) {
    throw new Unreadable('gate');
  }
  // An answer is words around its counts: tokens alone say nothing about what was counted.
  if (!parts.some((part) => part.t === 'text' && /\p{L}/u.test(part.text))) {
    throw new Unreadable('document');
  }

  const values = briefValuesOf(handles);

  const resolve = (token: BriefToken): BriefProseSegment => {
    const resolved = resolveBriefToken(token, handles, everything);
    if (resolved === null) {
      throw new Unreadable('token');
    }
    if (!briefTokenInScope(token, scope)) {
      throw new Unreadable('scope');
    }
    return resolved;
  };

  const segments = parts.map((part): FollowUpTextSegment => {
    switch (part.t) {
      case 'text':
        return part;
      case 'token':
        return valuedBriefSegment(resolve(part.token), values);
      case 'extra': {
        if (evidence === null) {
          throw new Error('an anyof is read only where its evidence is given');
        }
        const named = [...new Map(part.token.map((token) => [briefToken(token), token])).values()];
        if (named.length < 2 || named.length > MAX_FOLLOW_UP_ANYOF_REFS) {
          throw new Unreadable('anyof');
        }
        const refs = named.map((token): CountRef => {
          const segment = resolve(token);
          if (segment.t !== 'count') {
            throw new Error('an anyof handle resolves to a count');
          }
          return segment.ref;
        });
        return { t: 'anyof', refs, value: followUpAnyOfCount(evidence, refs) };
      }
    }
  });

  const bounded = boundedBriefProse(segments, MAX_FOLLOW_UP_ANSWER_CHARS);
  if (bounded.length > MAX_RICH_TEXT_SEGMENTS) {
    throw new Unreadable('segments');
  }
  return bounded;
};

/**
 * A passage of a follow-up job's own words that a Brief stores — an "Update the brief" revision's
 * change note — read as an answer's words are (`readFollowUpText`): the numeral gate, words of its
 * own around its counts, the Brief's tokens resolved across the whole study and valued from the
 * handles, cut to `MAX_FOLLOW_UP_ANSWER_CHARS`. No `anyof`, which no Brief can hold. `null` for
 * anything else.
 */
export const readFollowUpNote = (note: string, handles: BriefHandles): RichText | null => {
  const everything = wholeStudyProseScope(handles, { themes: 'kept' });
  try {
    const text = readFollowUpText(note, { handles, evidence: null, everything, scope: everything });
    const stored = richTextSchema.safeParse(text);
    return stored.success ? stored.data : null;
  } catch (error) {
    if (error instanceof Unreadable) {
      return null;
    }
    throw error;
  }
};

/** A note under a card: the follow-up it came from, and its answer's text and quotes. */
export interface FollowUpNote {
  followUpId: string;
  rqId: string;
  finalizedAt: string;
  question: string;
  text: FollowUpText;
  quotes: FollowUpQuote[];
}

/** One line of the Appendix's follow-up log: the question, and how it landed. */
export interface FollowUpLogEntry {
  followUpId: string;
  question: string;
  finalizedAt: string;
  outcome: FollowUpOutcome;
}

/**
 * What a Brief's follow-ups add to it as it stands now: the notes under its cards, the Appendix
 * log of every finalized follow-up oldest first, and how many there are. Past versions show none
 * of it.
 */
export interface FollowUpRecord {
  notes: FollowUpNote[];
  log: FollowUpLogEntry[];
  followUpCount: number;
}

/** A finalized follow-up, as the record is built from it. */
export interface FinalizedFollowUp {
  followUpId: string;
  question: string;
  answer: StoredFollowUpAnswer;
  outcome: FollowUpOutcome;
  finalizedAt: string;
}

/**
 * The record finalized follow-ups make: every one a log line, oldest first, each note also under
 * its card, and the count of all of them.
 */
export const followUpRecordOf = (finalized: readonly FinalizedFollowUp[]): FollowUpRecord => {
  const ordered = finalized.toSorted((left, right) =>
    left.finalizedAt.localeCompare(right.finalizedAt),
  );

  return {
    notes: ordered.flatMap((each) =>
      each.outcome.kind === 'note'
        ? [
            {
              followUpId: each.followUpId,
              rqId: each.outcome.rqId,
              finalizedAt: each.finalizedAt,
              question: each.question,
              text: each.answer.text,
              quotes: each.answer.quotes,
            },
          ]
        : [],
    ),
    log: ordered.map((each) => ({
      followUpId: each.followUpId,
      question: each.question,
      finalizedAt: each.finalizedAt,
      outcome: each.outcome,
    })),
    followUpCount: ordered.length,
  };
};

/** One follow-up as the Brief step's thread shows it. */
export interface FollowUpView {
  id: string;
  question: string;
  /** The card it was asked about, or `null` for the whole study. */
  rqId: string | null;
  askedAt: string;
  /** The Brief version it was asked against. */
  briefVersion: number;
  state: FollowUpState;
  answer: StoredFollowUpAnswer | null;
  answerReason: FollowUpFailureReason | null;
  retryable: boolean;
  updateReason: FollowUpFailureReason | null;
  /**
   * Whether an update being written has no job left on the queue to write it, so choosing "Update
   * the brief" again asks anew rather than waiting; `false` in every other state.
   */
  updateRetryable: boolean;
  outcome: FollowUpOutcome | null;
}
